// IntakeScreen — Intake-first wizard entry point (MANUS-WIZARD-V2 Task 1 + Task 4)
// Three questions determine the user's wizard path before showing any config steps.
// "Something else" triggers Claude classification + CustomPathBuilder (Task 4).
import { useState, useMemo, useCallback } from 'react';
import type { ProjectType, DeployTarget, DatabaseChoice, IntakeAnswers, WizardStep } from '../../lib/path-matrix';
import CustomPathBuilder from './CustomPathBuilder';
import api from '../../api';
export type { ProjectType, DeployTarget, DatabaseChoice, IntakeAnswers };

interface IntakeScreenProps {
  onStart: (answers: IntakeAnswers) => void;
  computeStepCount: (answers: IntakeAnswers) => number;
  computeStepNames: (answers: IntakeAnswers) => string[];
}

const PROJECT_TYPES: { value: ProjectType; label: string; icon: string; desc: string }[] = [
  { value: 'saas', label: 'SaaS / Web App', icon: '\u{1F310}', desc: 'Full-stack web application with users, billing, and deployment' },
  { value: 'internal', label: 'Internal Tool', icon: '\u{1F527}', desc: 'Internal dashboard, admin panel, or team utility' },
  { value: 'api', label: 'API / Backend', icon: '\u26A1', desc: 'REST or GraphQL API with server deployment' },
  { value: 'cli', label: 'CLI Tool', icon: '\u{1F4BB}', desc: 'Command-line tool distributed via npm, pip, or binary' },
  { value: 'static', label: 'Static Site', icon: '\u{1F4C4}', desc: 'Marketing site, docs, or blog with static hosting' },
  { value: 'custom', label: 'Something else', icon: '\u2728', desc: 'Describe your project and we\'ll suggest a setup' },
];

const DEPLOY_TARGETS: { value: DeployTarget; label: string }[] = [
  { value: 'railway', label: 'Railway (recommended)' },
  { value: 'flyio', label: 'Fly.io' },
  { value: 'render', label: 'Render' },
  { value: 'vps', label: 'Self-hosted / VPS' },
  { value: 'unsure', label: 'Not sure yet' },
];

const DATABASE_CHOICES: { value: DatabaseChoice; label: string }[] = [
  { value: 'supabase', label: 'Yes \u2014 Postgres (Supabase)' },
  { value: 'own', label: 'Yes \u2014 I have my own' },
  { value: 'none', label: 'No' },
];

export default function IntakeScreen({ onStart, computeStepCount, computeStepNames }: IntakeScreenProps) {
  const [projectType, setProjectType] = useState<ProjectType | null>(null);
  const [deployTarget, setDeployTarget] = useState<DeployTarget>('railway');
  const [databaseChoice, setDatabaseChoice] = useState<DatabaseChoice>('supabase');
  const [customDescription, setCustomDescription] = useState('');

  // ─── Task 4: Classification state ────────────────────────────
  const [classifying, setClassifying] = useState(false);
  const [classifyError, setClassifyError] = useState<string | null>(null);
  const [classifyResult, setClassifyResult] = useState<{
    suggestedType: string;
    services: string[];
    reasoning: string;
  } | null>(null);
  const [customPath, setCustomPath] = useState<WizardStep[]>(['github', 'deploy_verify']);

  // Conditional visibility: CLI and Static hide Q2 and Q3
  const showDeployQuestion = projectType !== null && projectType !== 'cli' && projectType !== 'static' && projectType !== 'custom';
  const showDatabaseQuestion = projectType !== null && projectType !== 'cli' && projectType !== 'static' && projectType !== 'custom';
  const showCustomInput = projectType === 'custom';

  // Build answers for preview (use defaults for hidden questions)
  const currentAnswers: IntakeAnswers | null = useMemo(() => {
    if (!projectType) return null;
    if (projectType === 'custom') {
      return {
        projectType,
        deployTarget: 'unsure' as DeployTarget,
        databaseChoice: 'none' as DatabaseChoice,
        customDescription: customDescription || undefined,
        customServices: customPath,
      };
    }
    return {
      projectType,
      deployTarget: showDeployQuestion ? deployTarget : 'unsure',
      databaseChoice: showDatabaseQuestion ? databaseChoice : 'none',
      customDescription: undefined,
    };
  }, [projectType, deployTarget, databaseChoice, showDeployQuestion, showDatabaseQuestion, customDescription, customPath]);

  const stepCount = currentAnswers ? computeStepCount(currentAnswers) : 0;
  const stepNames = currentAnswers ? computeStepNames(currentAnswers) : [];

  // For standard types, can start immediately. For custom, need classification or manual path.
  const canStart = projectType !== null && (
    projectType !== 'custom' || (classifyResult !== null || classifyError !== null)
  );

  // ─── Task 4: Classify handler (AC-22) ────────────────────────
  const handleClassify = useCallback(async () => {
    if (!customDescription.trim() || customDescription.trim().length < 3) return;
    setClassifying(true);
    setClassifyError(null);
    setClassifyResult(null);
    try {
      const result = await api.wizardClassify(customDescription.trim());
      setClassifyResult(result);
    } catch (err: unknown) {
      // AC-28: Fallback to manual toggle selection on failure
      console.error('[IntakeScreen] Classification failed:', err);
      setClassifyError(
        err instanceof Error ? err.message : 'Classification failed. You can manually select services below.'
      );
    } finally {
      setClassifying(false);
    }
  }, [customDescription]);

  // ─── Task 4: Custom path change handler (AC-27) ──────────────
  const handleCustomPathChange = useCallback((path: WizardStep[]) => {
    setCustomPath(path);
  }, []);

  return (
    <div style={{
      maxWidth: 640,
      margin: '0 auto',
      padding: '40px 24px',
    }}>
      <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
        Let's set up OCNexus for your project.
      </h2>
      <p style={{ margin: '0 0 32px', fontSize: 14, color: 'var(--text-secondary)' }}>
        Answer a few questions and we'll build a custom setup path for you.
      </p>

      {/* Q1: What are you building? */}
      <div style={{ marginBottom: 28 }}>
        <label style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
          What are you building?
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 10,
        }}>
          {PROJECT_TYPES.map(pt => (
            <button
              key={pt.value}
              onClick={() => {
                setProjectType(pt.value);
                // Reset classification state when switching away from custom
                if (pt.value !== 'custom') {
                  setClassifyResult(null);
                  setClassifyError(null);
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                padding: '16px 12px',
                border: `2px solid ${projectType === pt.value ? 'var(--blue)' : 'var(--border)'}`,
                borderRadius: 10,
                background: projectType === pt.value ? 'var(--blue-bg, rgba(59,130,246,0.08))' : 'var(--surface)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ fontSize: 24 }}>{pt.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{pt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom description input (revealed when "Something else" selected) — AC-21 */}
      {showCustomInput && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8 }}>
            Describe what you're building:
          </label>
          <input
            type="text"
            value={customDescription}
            onChange={e => { setCustomDescription(e.target.value); setClassifyResult(null); setClassifyError(null); }}
            placeholder="e.g., a Chrome extension with a Node backend for processing"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 14,
              background: 'var(--surface)',
              color: 'var(--text)',
              boxSizing: 'border-box',
            }}
          />

          {/* Analyze button — triggers Claude classification (AC-22) */}
          {!classifyResult && !classifyError && (
            <button
              onClick={handleClassify}
              disabled={classifying || customDescription.trim().length < 3}
              style={{
                marginTop: 10,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                border: 'none',
                borderRadius: 8,
                background: classifying || customDescription.trim().length < 3
                  ? 'var(--border)' : 'var(--blue)',
                color: classifying || customDescription.trim().length < 3
                  ? 'var(--text-secondary)' : '#fff',
                cursor: classifying || customDescription.trim().length < 3
                  ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {classifying ? 'Analyzing...' : 'Analyze \u2192'}
            </button>
          )}

          {/* Classification error — fallback to manual toggles (AC-28) */}
          {classifyError && (
            <div style={{
              marginTop: 12,
              padding: '10px 14px',
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--text)',
            }}>
              {classifyError}
            </div>
          )}
        </div>
      )}

      {/* Classification result + CustomPathBuilder (AC-23, AC-24) */}
      {showCustomInput && (classifyResult || classifyError) && (
        <div style={{ marginBottom: 28 }}>
          {classifyResult && (
            <div style={{
              marginBottom: 12,
              padding: '10px 14px',
              background: 'rgba(34,197,94,0.08)',
              border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--text)',
            }}>
              Suggested type: <strong>{classifyResult.suggestedType}</strong>
            </div>
          )}
          <CustomPathBuilder
            suggestedServices={classifyResult?.services ?? []}
            reasoning={classifyResult?.reasoning}
            onPathChange={handleCustomPathChange}
          />
        </div>
      )}

      {/* Q2: Where do you want to deploy? (hidden for custom type) */}
      {showDeployQuestion && (
        <div style={{ marginBottom: 28 }}>
          <label style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
            Where do you want to deploy?
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {DEPLOY_TARGETS.map(dt => (
              <label
                key={dt.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  border: `1px solid ${deployTarget === dt.value ? 'var(--blue)' : 'var(--border)'}`,
                  borderRadius: 8,
                  background: deployTarget === dt.value ? 'var(--blue-bg, rgba(59,130,246,0.08))' : 'var(--surface)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <input
                  type="radio"
                  name="deploy-target"
                  value={dt.value}
                  checked={deployTarget === dt.value}
                  onChange={() => setDeployTarget(dt.value)}
                  style={{ accentColor: 'var(--blue)' }}
                />
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{dt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Q3: Do you need a database? (hidden for custom type) */}
      {showDatabaseQuestion && (
        <div style={{ marginBottom: 28 }}>
          <label style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
            Do you need a database?
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {DATABASE_CHOICES.map(dc => (
              <label
                key={dc.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  border: `1px solid ${databaseChoice === dc.value ? 'var(--blue)' : 'var(--border)'}`,
                  borderRadius: 8,
                  background: databaseChoice === dc.value ? 'var(--blue-bg, rgba(59,130,246,0.08))' : 'var(--surface)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <input
                  type="radio"
                  name="database-choice"
                  value={dc.value}
                  checked={databaseChoice === dc.value}
                  onChange={() => setDatabaseChoice(dc.value)}
                  style={{ accentColor: 'var(--blue)' }}
                />
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{dc.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Step Preview (for non-custom types) */}
      {projectType && projectType !== 'custom' && (
        <div style={{
          marginBottom: 28,
          padding: '16px 20px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 10,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            Based on your answers, you'll need {stepCount} setup step{stepCount !== 1 ? 's' : ''}:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {stepNames.map((name, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  fontSize: 12,
                  fontWeight: 500,
                  borderRadius: 6,
                  background: 'var(--blue-bg, rgba(59,130,246,0.08))',
                  color: 'var(--blue)',
                  border: '1px solid var(--blue)',
                }}
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Start Setup button */}
      <button
        disabled={!canStart}
        onClick={() => {
          if (!currentAnswers) return;
          onStart(currentAnswers);
        }}
        style={{
          width: '100%',
          padding: '14px 24px',
          fontSize: 15,
          fontWeight: 600,
          border: 'none',
          borderRadius: 10,
          background: canStart ? 'var(--blue)' : 'var(--border)',
          color: canStart ? '#fff' : 'var(--text-secondary)',
          cursor: canStart ? 'pointer' : 'not-allowed',
          transition: 'all 0.15s ease',
        }}
      >
        Start Setup \u2192
      </button>
    </div>
  );
}
